import { getExpiry, PermitType } from "../permitUtils";
import { useCallback } from "react";
import { MAX_UINT_256, PERMIT_TWO_CONTRACT_ADDRESS } from "../../config/consts";
import { approveViaPermit1, getPermit1Nonce } from "../permit1Utils";
import { GlobalStore, SelectedToken } from "../../store/global/GlobalStore";
import {
  getAbiToUse,
  getApprovalAmountFromReceipt,
  getPolygonPublicGasEstimate,
} from "../allowanceUtils";
import * as Sentry from "@sentry/react";
import { getAccount, prepareWriteContract, waitForTransaction, writeContract } from "wagmi/actions";
import { polygon } from "wagmi/chains";
import { usePublicClient } from "wagmi";
import { Hash, TransactionReceipt } from "viem";
import { Amount } from "../../types/amount";
import { tryNTimes } from "../tryUtils";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import { isAddressEqual } from "../tokenUtils";

export const useApproveViaPermits = (permitType: PermitType, approvalTarget: Hash | undefined) => {
  const tokenList = useTokenListByChain();
  const wagmiAccount = getAccount();
  const { network, userWallet, takerTokens } = GlobalStore.useState(s => {
    return {
      network: s.network,
      userWallet: s.address,
      takerTokens: s.takerTokens,
    };
  });
  const publicClient = usePublicClient();
  const approve = useCallback(
    async (
      tokenAddress: Hash,
      onTransaction?: (hash: `0x${string}`) => void,
      onApproved?: (newAmount: Amount) => void,
      shouldRevoke = false
    ) => {
      if (tokenList === undefined) {
        console.log("No metadata available, cannot approve");
        return;
      }
      console.log(`APPROVE WAS CALLED WITH PERMIT TYPE: ${permitType}`);
      const tokenData = tokenList[tokenAddress];
      if (permitType === "permit1" && tokenData && approvalTarget) {
        const nonce = await getPermit1Nonce(tokenAddress, userWallet as `0x${string}`);
        if (nonce === undefined) {
          throw new Error(`Could not get nonce for token ${tokenAddress}`);
        }
        console.log(`Got nonce: ${nonce}`);
        return approveViaPermit1({
          tokenAddress: tokenAddress,
          owner: userWallet as `0x${string}`,
          spender: approvalTarget,
          value: MAX_UINT_256,
          nonce,
          deadline: getExpiry(),
          chainId: network.id,
          tokenData,
        });
      } else {
        /**
         * Permit2 Flow
         */
        const selectedToken: SelectedToken | undefined = takerTokens.find(t =>
          isAddressEqual(t.tokenAddress, tokenAddress)
        );
        if (!selectedToken) {
          console.log(`Could not find token ${tokenAddress} in selected tokens`);
          return;
        }
        console.log(`APPROVING VIA PERMIT2`);
        // First we call .approve() on the token contract to give the Permit2 contract an allowance.
        const { request } = await prepareWriteContract({
          address: tokenAddress,
          // @ts-expect-error The non-standard erc20 abi has a different structure
          abi: getAbiToUse(network, tokenAddress),
          functionName: "approve",
          args: [PERMIT_TWO_CONTRACT_ADDRESS, shouldRevoke ? BigInt(0) : MAX_UINT_256],
        });

        let polyGasEstimateIfApplicable = {};
        if (network.id === polygon.id) {
          const [gasPriceEstimate, gas] = await Promise.all([
            getPolygonPublicGasEstimate(network),
            publicClient.estimateContractGas(request),
          ]);
          polyGasEstimateIfApplicable = {
            ...gasPriceEstimate,
            gas,
          };
        }
        const accountFieldsToLog = {
          address: wagmiAccount.address,
          connector: wagmiAccount.connector?.name,
          status: wagmiAccount.status,
        };
        console.log(
          "Approval request",
          request,
          "polygonGasEstimate",
          polyGasEstimateIfApplicable,
          "wagmiAccount",
          JSON.stringify(accountFieldsToLog)
        );
        const { hash } = await writeContract({ ...request, ...polyGasEstimateIfApplicable });
        onTransaction?.(hash);
        console.log(`Starting to wait for tx for token ${tokenAddress} with hash ${hash}`);
        const txData = await tryNTimes<TransactionReceipt>({
          toTry: () => waitForTransaction({ hash }),
          times: 3,
          interval: 1,
        });
        // Check the transaction receipt to see how much was approved.
        const reapprovedAmount = getApprovalAmountFromReceipt(txData, tokenAddress, tokenList);
        onApproved?.(reapprovedAmount);
        Sentry.captureMessage(`Approved ${tokenAddress})`);
        console.log(`Got approval txData: ${JSON.stringify(txData)}`);
        const expiry = getExpiry();
        return {
          type: "Permit2",
          signature: undefined,
          txData,
          deadline: expiry,
          nonce: undefined,
        };
      }
    },
    [
      tokenList,
      permitType,
      network,
      takerTokens,
      approvalTarget,
      userWallet,
      publicClient,
      wagmiAccount,
    ]
  );

  return {
    approve,
  };
};
